import React, { useState } from 'react'
import { Search, ArrowLeft, Users, Loader2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
import { KidsEditForm } from './KidsEditForm'

interface KidSearchResult {
  id: string
  nom: string
  prenom: string
  date_naissance: string
  n_national?: string
  ecole?: string
  user_id: string
  user?: {
    nom?: string
    prenom?: string
    email: string
  }
}

interface KidsEditProps {
  onBack: () => void
}

export function KidsEdit({ onBack }: KidsEditProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<KidSearchResult[]>([])
  const [searched, setSearched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedKidId, setSelectedKidId] = useState<string | null>(null)

  const handleSearch = async () => {
    const term = searchTerm.trim()
    if (!term) return

    setLoading(true)
    setSearched(true)
    try {
      const like = `%${term}%`

      const { data: byKidName } = await supabase
        .from('kids')
        .select('id, nom, prenom, date_naissance, n_national, ecole, user_id, users!kids_user_id_fkey(nom, prenom, email)')
        .or(`nom.ilike.${like},prenom.ilike.${like}`)
        .limit(50)

      const { data: byParent } = await supabase
        .from('kids')
        .select('id, nom, prenom, date_naissance, n_national, ecole, user_id, users!kids_user_id_fkey(nom, prenom, email)')
        .or(`users.nom.ilike.${like},users.prenom.ilike.${like},users.email.ilike.${like}`, { referencedTable: 'users' })
        .limit(50)

      const merged = new Map<string, KidSearchResult>()
      for (const raw of [...(byKidName || []), ...(byParent || [])]) {
        if (!merged.has(raw.id)) {
          merged.set(raw.id, {
            ...raw,
            user: raw.users as any,
          } as KidSearchResult)
        }
      }

      const sorted = Array.from(merged.values()).sort((a, b) =>
        `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`)
      )
      const uuids = [...new Set(sorted.map(k => k.ecole).filter((e): e is string => !!e && UUID_RE.test(e)))]
      if (uuids.length > 0) {
        const { data: schools } = await supabase.from('schools').select('id, name').in('id', uuids)
        if (schools) {
          const map = new Map(schools.map(s => [s.id, s.name]))
          for (const kid of sorted) { if (kid.ecole && map.has(kid.ecole)) kid.ecole = map.get(kid.ecole)! }
        }
      }
      setResults(sorted)
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const calculateAge = (dateNaissance: string) => {
    const birth = new Date(dateNaissance)
    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    const m = today.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
    return age
  }

  const formatDate = (d: string) => {
    return new Date(d).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  if (selectedKidId) {
    return (
      <KidsEditForm
        kidId={selectedKidId}
        onBack={() => setSelectedKidId(null)}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button
          onClick={onBack}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="p-2 bg-amber-100 rounded-lg">
          <Users className="h-6 w-6 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Modifier un enfant</h1>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Rechercher par nom, prenom, nom du parent ou email
        </label>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ex: Dupont, Marie, parent@email.com..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !searchTerm.trim()}
            className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Rechercher
          </button>
        </div>
      </div>

      {/* Results */}
      {searched && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-700">
              <span className="font-medium">{results.length}</span> enfant{results.length !== 1 ? 's' : ''} trouve{results.length !== 1 ? 's' : ''}
            </p>
          </div>

          {results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enfant</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date de naissance</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Ecole</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {results.map((kid) => (
                    <tr
                      key={kid.id}
                      onClick={() => setSelectedKidId(kid.id)}
                      className="hover:bg-amber-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{kid.prenom} {kid.nom}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{formatDate(kid.date_naissance)}</div>
                        <div className="text-xs text-gray-500">{calculateAge(kid.date_naissance)} ans</div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-sm text-gray-700">{kid.ecole || '-'}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{kid.user?.prenom} {kid.user?.nom}</div>
                        <div className="text-xs text-gray-500">{kid.user?.email}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10">
              <Users className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">Aucun enfant ne correspond a cette recherche.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
